/**
 * Who at the company was actually on the call.
 *
 * The company answers "whose call was this"; this answers "who do I ring back".
 * Until `CallLog.contactId` existed the second question had no column, so it
 * lived in the operator's note as the words "ask for Mart" - readable by a
 * human, invisible to every list, and gone the moment somebody rewrote the note.
 *
 * Its own file rather than more lines in `CallLinks`, for the reason
 * `CallLeadStatus` is its own file: the strip is getting crowded and each
 * control there is a separate decision about the call.
 *
 * ⚠ ONLY SHOWN ON A CALL THAT NAMES A CLIENT, and that is the data model rather
 * than a design choice: a `Contact` hangs off exactly one `Client`, so without a
 * company there is no set of people to choose from. A juhuklient has no contacts
 * at all - the lead row IS the person - which is why attaching a lead hides this
 * control instead of emptying it.
 *
 * The list is fetched on the first OPEN, not on mount. Every pane in the call
 * list would otherwise pay for a request that most calls never need, and the
 * attached person is already on the row.
 */
import { useState } from "react";
import { Loader2, User } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { getClientContacts, type Contact } from "../../../lib/clients";
import type { CallContactRef } from "../../../lib/callTypes";

/**
 * "Nobody in particular", as a value Radix will accept.
 *
 * An empty string is not one: `SelectItem` refuses it, because that is the
 * value the trigger uses to mean "nothing selected". So the two states need two
 * different tokens, and detaching sends `null` over the wire regardless.
 */
const NOBODY = "__none__";

/** The two halves joined, without the trailing space a null surname would leave. */
export function contactName(c: { firstName: string; lastName?: string | null }): string {
  return `${c.firstName} ${c.lastName ?? ""}`.trim();
}

export function CallPerson({
  clientId,
  contact,
  onPick,
}: {
  /** The company on the call. The contacts offered are this client's own. */
  clientId: string;
  /** Who is attached now, straight off the call row. Null = nobody yet. */
  contact: CallContactRef | null;
  /** `null` detaches. The server moves nothing else - the company stays. */
  onPick: (contactId: string | null) => void;
}) {
  const t = useT();
  const [rows, setRows] = useState<Contact[] | null>(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    if (rows !== null || loading) return;
    setLoading(true);
    try {
      const res = await getClientContacts(clientId);
      setRows(res.rows);
    } catch {
      // An empty list rather than an error state: this control is a shortcut,
      // and the person already attached is still shown and still detachable.
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  // The attached person is drawn from the ROW, so the trigger reads correctly
  // before the list has ever been fetched. Without this the select would show
  // an empty box on the first paint of every call that already names somebody.
  const known = new Map<string, string>();
  if (contact) known.set(contact.id, contactName(contact));
  for (const r of rows ?? []) known.set(r.id, contactName(r));

  return (
    <Select
      value={contact?.id ?? NOBODY}
      onValueChange={(v) => onPick(v === NOBODY ? null : v)}
      onOpenChange={(open) => open && void load()}
    >
      <SelectTrigger className="h-7 w-[180px] text-xs" aria-label={t("calls.detail.person")}>
        <User className="size-3.5 shrink-0 text-muted-foreground" />
        <SelectValue placeholder={t("calls.detail.personNone")} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NOBODY} className="text-xs">
          {t("calls.detail.personNone")}
        </SelectItem>
        {[...known].map(([id, name]) => (
          <SelectItem key={id} value={id} className="text-xs">
            {name}
          </SelectItem>
        ))}
        {loading ? (
          <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" />
            {t("calls.detail.personLoading")}
          </div>
        ) : null}
        {/* Said out loud rather than left as an empty menu: "this company has
            nobody on file" and "the list failed to load" look identical from
            the outside, and only one of them is worth doing something about. */}
        {rows !== null && rows.length === 0 && !contact ? (
          <div className="px-3 py-2 text-xs text-muted-foreground">{t("calls.detail.personEmpty")}</div>
        ) : null}
      </SelectContent>
    </Select>
  );
}
